/**
 * @fileoverview Translate a host ImportRun into the shared RunPollProgress shape.
 * @module import-editor/unifiedWizard/runToProgress
 *
 * Responsibilities:
 * - Project the host-specific `ImportRun` (tenant-web or system-web) into the
 *   framework-agnostic `RunPollProgress` consumed by UnifiedImportWizard.
 * - Default missing summary fields to zeros so the progress indicator always
 *   has a complete shape to render, even before the first flushProgress tick.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for the `ImportRun` type
 * - `./services` for the `RunPollProgress` contract
 *
 * Architecture:
 * - Pure projection function — no I/O, no side effects, no host wiring.
 * - Reads only well-known fields on the run via `as any` accessors because
 *   the host `ImportRun` shape carries summary/progress fields as optional
 *   extensions not present on the shared type.
 *
 * Notes:
 * - Both tenant-web and system-web adapters previously carried verbatim copies
 *   of this projection. It lives here now so the phase-mapping and zero-default
 *   rules cannot drift between hosts.
 */

import type { ImportRun } from '@firesquirrel/local-db';
import type { RunPollProgress } from './services';

/**
 * @reusable runToProgress
 * Usage:        Called by host UnifiedImportWizard adapters inside their
 *               `pollRun` onProgress callback to feed the shared progress UI.
 * Rules:        Presentation-only projection. Does not mutate the run.
 *
 * Translate a host `ImportRun` into the shared `RunPollProgress` shape.
 *
 * The summary fields are optional on the run until the first flushProgress
 * tick lands, so missing counters default to zero while the phase is always
 * derived from `run.status`.
 */
export function runToProgress(run: ImportRun): RunPollProgress {
  const s = (run as any).summary ?? null;
  const rawStatus = (run as any).status as string;
  const phase: RunPollProgress['phase'] =
    rawStatus === 'completed' || rawStatus === 'dry_run_completed'
      ? 'done'
      : rawStatus === 'failed'
        ? 'failed'
        : rawStatus === 'cancelled'
          ? 'cancelled'
          : rawStatus === 'queued'
            ? 'queued'
            : 'running';
  return {
    phase,
    rawStatus,
    startedAt: (run as any).startedAt,
    stage: (run as any).progressStage,
    stageDetail: (run as any).progressDetail,
    summary: s
      ? {
          totalRows: s.totalRows ?? 0,
          created: s.created ?? 0,
          updated: s.updated ?? 0,
          softDeleted: s.softDeleted ?? 0,
          excluded: {
            outOfJurisdiction: s.excluded?.outOfJurisdiction ?? 0,
            missingCoordinates: s.excluded?.missingCoordinates ?? 0,
            duplicateInsertOnly: s.excluded?.duplicateInsertOnly ?? 0,
            missingMatchKey: s.excluded?.missingMatchKey ?? 0,
          },
          failedByKind: {
            transform: s.failedByKind?.transform ?? 0,
            entityBuild: s.failedByKind?.entityBuild ?? 0,
          },
        }
      : null,
  };
}
